import { sprintf } from "../slideshow/playlist";
import { LINK_SHARE_URLS } from "../slideshow/share";
import { type SlideshowEvent, track } from "../slideshow/track";
import { slideshowTexts } from "./useSlideshow";

export type Service =
  | "facebook"
  | "messenger"
  | "whatsapp"
  | "linkedin"
  | "email";

const SERVICES: Service[] = [
  "facebook",
  "messenger",
  "whatsapp",
  "linkedin",
  "email",
];
const SERVICE_NAMES: Record<Exclude<Service, "email">, string> = {
  facebook: "Facebook",
  linkedin: "LinkedIn",
  messenger: "Messenger",
  whatsapp: "WhatsApp",
};
// Font Awesome Free 6.5.2 (CC BY 4.0), the version the theme's other icons come from.
const ICON = (viewBox: string, d: string) =>
  `<svg viewBox="${viewBox}" aria-hidden="true"><path fill="currentColor" d="${d}"/></svg>`;
const SERVICE_ICONS: Record<Service, string> = {
  email: ICON(
    "0 0 512 512",
    "M64 112c-8.8 0-16 7.2-16 16v22.1L220.5 291.7c20.7 17 50.4 17 71.1 0L464 150.1V128c0-8.8-7.2-16-16-16H64zM48 212.2V384c0 8.8 7.2 16 16 16H448c8.8 0 16-7.2 16-16V212.2L322 328.8c-38.4 31.5-93.7 31.5-132 0L48 212.2zM0 128C0 92.7 28.7 64 64 64H448c35.3 0 64 28.7 64 64V384c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V128z"
  ),
  facebook: ICON(
    "0 0 512 512",
    "M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256C0 376 82.7 476.8 194.2 504.5V334.2H141.4V256h52.8V222.3c0-87.1 39.4-127.5 125-127.5c16.2 0 44.2 3.2 55.7 6.4V172c-6-.6-16.5-1-29.6-1c-42 0-58.2 15.9-58.2 57.2V256h83.6l-14.4 78.2H287V510.1C413.8 494.8 512 386.9 512 256h0z"
  ),
  linkedin: ICON(
    "0 0 448 512",
    "M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"
  ),
  messenger: ICON(
    "0 0 512 512",
    "M256.55 8C116.52 8 8 110.34 8 248.57c0 72.3 29.71 134.78 78.07 177.94 8.35 7.51 6.63 11.86 8.05 58.23A19.92 19.92 0 0 0 122 502.31c52.91-23.3 53.59-25.14 62.56-22.7C337.85 521.8 504 423.7 504 248.57 504 110.34 396.59 8 256.55 8zm149.24 185.13l-73 115.57a37.37 37.37 0 0 1-53.91 9.93l-58.08-43.47a15 15 0 0 0-18 0l-78.37 59.44c-10.46 7.93-24.16-4.6-17.11-15.67l73-115.57a37.36 37.36 0 0 1 53.91-9.93l58.06 43.46a15 15 0 0 0 18 0l78.41-59.38c10.44-7.98 24.14 4.54 17.09 15.62z"
  ),
  whatsapp: ICON(
    "0 0 448 512",
    "M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"
  ),
};
const POPUP_FEATURES = "noopener,width=640,height=640";

/** The services the link can be shared to from a computer: the share card and the film's end. */
export function useShareServices(
  urlFor: (medium: string) => string,
  textFor: (medium: string) => string,
  name: () => string,
  copyFallback: () => void,
  surface: SlideshowEvent["surface"],
  /** Instead of the slideshow's `link_shared` (the memory book). */
  onShared?: (service: Service) => void
) {
  const texts = slideshowTexts();

  const serviceLabel = (s: Service) =>
    s === "email" ? texts.email : SERVICE_NAMES[s];

  /** Open the link where people are, inside the click so no pop-up blocker gets in the way. */
  function shareTo(service: Service) {
    const url = urlFor(service);
    if (service === "messenger") {
      const fb = (
        window as Window & {
          FB?: { ui: (o: Record<string, string>) => void };
        }
      ).FB;
      if (fb) {
        fb.ui({ link: url, method: "send" });
      } else {
        copyFallback();
      }
    } else if (service === "email") {
      const subject = sprintf(texts.emailSubject, name());
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(textFor(service))}`;
    } else {
      const href =
        service === "whatsapp"
          ? LINK_SHARE_URLS.whatsapp(textFor(service))
          : LINK_SHARE_URLS[service](url);
      window.open(href, "_blank", POPUP_FEATURES);
    }
    if (onShared) {
      onShared(service);
    } else {
      track({ action: "link_shared", platform: service, surface });
    }
  }

  return { SERVICE_ICONS, SERVICES, serviceLabel, shareTo };
}
